import type {
  ConditionTrigger,
  TriggerOccurrence,
  TriggerStateRow
} from '@axilon-apps/domains-ontology-ui-state'

import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import {
  deriveStoredTriggerDisplay,
  pendingTiming,
  type TriggerDisplayState
} from './trigger-state-display'

function duration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms % 1000 === 0 ? 0 : 1)}s`
  return `${(ms / 60_000).toFixed(1)}m`
}

function date(value?: string | null): string {
  if (!value) return 'Never'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

function displayPriority(state: TriggerDisplayState, hasOccurrence: boolean): number {
  if (
    hasOccurrence ||
    state === 'active' ||
    state === 'pending_clear' ||
    state === 'active_stale'
  ) {
    return 0
  }
  if (state === 'pending_activation') return 1
  if (state === 'unavailable') return 2
  return 3
}

function cardClasses(state: TriggerDisplayState): string {
  switch (state) {
    case 'active':
    case 'pending_clear':
    case 'active_stale':
      return 'border-st-down/40 bg-st-down-bg/20'
    case 'pending_activation':
      return 'border-st-warn/40 bg-st-warn-bg/20'
    case 'normal':
      return 'border-st-run/30 bg-st-run-bg/20'
    case 'unavailable':
      return 'border-line bg-card'
  }
}

function badgeClasses(state: TriggerDisplayState): string {
  switch (state) {
    case 'active':
    case 'pending_clear':
    case 'active_stale':
      return 'bg-st-down-bg text-st-down'
    case 'pending_activation':
      return 'bg-st-warn-bg text-st-warn'
    case 'normal':
      return 'bg-st-run-bg text-st-run'
    case 'unavailable':
      return 'bg-bg-sunk text-ink-2'
  }
}

export function StoredTriggerStateList({
  triggers,
  states,
  occurrences = [],
  onOccurrenceAction,
  actionBusy = false
}: {
  triggers: ConditionTrigger[]
  states: TriggerStateRow[]
  occurrences?: TriggerOccurrence[]
  onOccurrenceAction?: (
    occurrenceId: string,
    action: 'acknowledge' | 'suppress' | 'unsuppress'
  ) => void
  actionBusy?: boolean
}) {
  const byId = new Map(states.map((state) => [state.trigger_id, state]))
  const activeOccurrenceByTrigger = new Map(
    occurrences
      .filter((occurrence) => !occurrence.cleared_at)
      .map((occurrence) => [occurrence.trigger_id, occurrence] as const)
  )
  const rows = triggers
    .map((trigger, index) => {
      const state = trigger.id ? byId.get(trigger.id) : undefined
      const occurrence = trigger.id ? activeOccurrenceByTrigger.get(trigger.id) : undefined
      const display = deriveStoredTriggerDisplay(state)
      return {
        trigger,
        index,
        state,
        occurrence,
        display,
        timing: state ? pendingTiming(state) : null
      }
    })
    .sort(
      (left, right) =>
        displayPriority(left.display.state, Boolean(left.occurrence)) -
          displayPriority(right.display.state, Boolean(right.occurrence)) ||
        left.index - right.index
    )

  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      {rows.map(({ trigger, index, state, occurrence, display, timing }) => {
        return (
          <div
            key={trigger.id ?? index}
            data-trigger-state={display.state}
            className={cn(
              'flex flex-col gap-1 rounded-lg border px-3 py-2',
              cardClasses(display.state)
            )}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-ink">
                {trigger.name?.trim() || `Trigger ${index + 1}`}
              </span>
              <span
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-medium',
                  badgeClasses(display.state)
                )}
              >
                {display.label}
              </span>
            </div>
            {timing && (
              <p className="m-0 text-xs text-ink-3">
                Pending for {duration(timing.elapsedMs)}
                {timing.targetMs !== undefined ? ` of ${duration(timing.targetMs)}` : ''}
              </p>
            )}
            {state && (
              <p className="m-0 text-xs text-ink-4">
                Activated: {date(state.activated_at)} · Last clear: {date(state.last_cleared_at)}
                {state.quality ? ` · Quality: ${state.quality}` : ''}
              </p>
            )}
            {occurrence && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className="text-xs text-ink-4">
                  {occurrence.suppress_until_clear
                    ? `Suppressed until normal${occurrence.suppressed_by ? ` by ${occurrence.suppressed_by}` : ''}`
                    : occurrence.acknowledged_at
                      ? `Acknowledged${occurrence.acknowledged_by ? ` by ${occurrence.acknowledged_by}` : ''}`
                      : 'Unacknowledged'}
                </span>
                {onOccurrenceAction && !occurrence.acknowledged_at && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={actionBusy}
                    onClick={() => onOccurrenceAction(occurrence.id, 'acknowledge')}
                  >
                    Acknowledge
                  </Button>
                )}
                {onOccurrenceAction && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={actionBusy}
                    onClick={() =>
                      onOccurrenceAction(
                        occurrence.id,
                        occurrence.suppress_until_clear ? 'unsuppress' : 'suppress'
                      )
                    }
                  >
                    {occurrence.suppress_until_clear ? 'Resume actions' : 'Suppress until normal'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
